import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-AU")}`);
const fmtDate = (iso) => (iso ? iso.split("-").reverse().join("/").replace(/\/20(\d\d)$/, "/$1") : "—");
const COLS = ["Post", "Mar.", "Venue", "Date", "KRI", "Race", "Class", "Age", "Sex", "Prize", "Dist.", "Con.", "Jockey", "Wgt.", "Bar.", "Rail", "Flucs", "Price", "Run", "Other Runners", "Off. Time", "PF Time"];

const Stat = ({ k, v }) => (
  <div className="flex justify-between gap-2 border-b border-slate-800/50 py-0.5"><span className="text-slate-500">{k}:</span><span className="num text-slate-200">{v ?? "—"}</span></div>
);

const Runs = ({ runs, testId }) => (
  <div className="scroll-x">
    <table className="w-full text-[11px] whitespace-nowrap" data-testid={`${testId}-runs`}>
      <thead><tr className="eyebrow text-left border-b border-slate-700">{COLS.map((c) => <th key={c} className="px-1.5 py-1 font-normal">{c}</th>)}</tr></thead>
      <tbody>
        {runs.map((r, i) => (
          <tr key={i} className="border-b border-slate-800/60 align-top" data-testid={`${testId}-run-${i}`}>
            <td className="px-1.5 py-1 num"><span className={r.position === 1 ? "text-emerald-400 font-bold" : "text-white"}>{r.position}-{r.starters}</span>
              {r.days_since != null && <div className="text-slate-500">{r.days_since}d</div>}</td>
            <td className="px-1.5 py-1 num">{r.position === 1 ? "—" : r.margin}</td>
            <td className="px-1.5 py-1" title={r.venue_name}>{r.venue}</td>
            <td className="px-1.5 py-1 num">{fmtDate(r.date)}</td>
            <td className="px-1.5 py-1 num">{r.kri ?? ""}</td>
            <td className="px-1.5 py-1 max-w-[180px] truncate" title={r.race}>{r.race}</td>
            <td className="px-1.5 py-1">{r.class}</td>
            <td className="px-1.5 py-1">{r.age || ""}</td>
            <td className="px-1.5 py-1">{r.sex || ""}</td>
            <td className="px-1.5 py-1 num">{money(r.prize)}</td>
            <td className="px-1.5 py-1 num">{r.distance}</td>
            <td className="px-1.5 py-1">{r.condition}</td>
            <td className="px-1.5 py-1">{r.jockey}</td>
            <td className="px-1.5 py-1 num">{r.weight}</td>
            <td className="px-1.5 py-1 num">{r.barrier}</td>
            <td className="px-1.5 py-1 num">{r.rail ?? ""}</td>
            <td className="px-1.5 py-1 num">{r.flucs || ""}</td>
            <td className="px-1.5 py-1 num text-amber-400">{r.price || ""}</td>
            <td className="px-1.5 py-1 num">{r.in_run || ""}</td>
            <td className="px-1.5 py-1">{r.others.join(", ")}</td>
            <td className="px-1.5 py-1 num">{r.official_time}</td>
            <td className="px-1.5 py-1 num">{r.pf_time}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const HorseForm = ({ raceId, number, testId }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/races/${raceId}/form`, { params: { number } }).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [raceId, number]);
  if (err) return <div className="text-xs text-slate-500 p-3" data-testid={`${testId}-error`}>{err}</div>;
  if (!d) return <div className="p-3"><div className="loader-bar" /></div>;
  const rec = d.records;
  return (
    <div className="bg-[#0B1120] border border-slate-800 rounded p-3 sm:p-4 space-y-3 text-xs whitespace-normal" data-testid={testId}>
      <div className="space-y-0.5 text-slate-300">
        <div><span className="num text-white font-bold">{d.record}</span>{d.trainer && <> - Trainer: <span className="text-white">{d.trainer}</span></>}</div>
        <div>{[d.age && `${d.age} yo`, d.colour, d.sex].filter(Boolean).join(" ")}{d.sire && ` by ${d.sire}`}{d.dam && ` - ${d.dam}`}</div>
        {d.owners && <div className="text-slate-400 break-words whitespace-normal">Owners: {d.owners}</div>}
        <div className="text-slate-400">Winning distances: <span className="text-slate-200">{d.winning_distances || "—"}</span> • Prize Money: <span className="text-slate-200">{money(d.prize_money)}</span></div>
      </div>
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-0.5" data-testid={`${testId}-stats`}>
        <Stat k="Career" v={d.record} /><Stat k="1st Up" v={rec["First Up"]} /><Stat k="2nd Up" v={rec["2nd Up"]} /><Stat k="Last 5" v={d.last5} />
        <Stat k="Win %" v={d.win_pct} /><Stat k="Place %" v={d.place_pct} /><Stat k="Track" v={rec.Track} /><Stat k="Distance" v={rec.Distance} />
        <Stat k="Track/Dist" v={rec["Track/Dist"]} /><Stat k="Firm" v={rec.Firm} /><Stat k="Good" v={rec.Good} /><Stat k="Soft" v={rec.Soft} />
        <Stat k="Heavy" v={rec.Heavy} /><Stat k="Synthetic" v={rec.Synthetic} /><Stat k="Jumps" v={rec.Jumps} /><Stat k="Group 1-3" v={[rec["Group 1"], rec["Group 2"], rec["Group 3"]].join(" / ")} />
      </div>
      {d.runs.length ? <Runs runs={d.runs} testId={testId} /> : <div className="text-slate-500">No official starts yet.</div>}
    </div>
  );
};
